import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { Loader2, Download, Share2, Lock, Award, Users, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const overline = "hr-overline text-blaze text-[11px]";

function Big({ value, label, sub }) {
  return (
    <div className="text-center px-2">
      <div className="font-serif text-5xl sm:text-6xl text-bone leading-none">{value}</div>
      <div className="text-bone/50 text-[11px] uppercase tracking-widest mt-2">{label}</div>
      {sub && <div className="text-bone/30 text-[10px] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function YearInReview() {
  const { isAuthed, ready } = useAuth();
  const { openAuth } = useUI();
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState({ partners: true, photos: true, harvests: true });
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);

  const load = (y) => { setLoading(true); api.yearInReview(y).then(setData).catch(() => setData(null)).finally(() => setLoading(false)); };
  useEffect(() => { if (isAuthed) load(year); }, [isAuthed, year]);

  const buildSummary = () => {
    if (!data) return "";
    const parts = [`My HomeRange ${data.year}:`, `${data.hunts} hunts`];
    if (data.days_known) parts.push(`${data.days_afield} days afield`);
    parts.push(`${data.states.length} states`, `${data.species.length} species`);
    if (show.harvests) parts.push(`${data.harvests} harvests`);
    return parts.join(" · ");
  };

  const share = async () => {
    const text = buildSummary();
    try {
      if (navigator.share) { await navigator.share({ title: `My HomeRange ${data.year}`, text }); }
      else { await navigator.clipboard.writeText(text); toast.success("Recap copied to clipboard."); }
    } catch { /* user cancelled */ }
  };

  const download = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#1a1a17", skipFonts: true });
      const a = document.createElement("a");
      a.download = `homerange-${data.year}.png`; a.href = url; a.click();
      toast.success("Recap image downloaded.");
    } catch { toast.error("Couldn't generate image."); }
    setExporting(false);
  };

  if (!ready || (isAuthed && loading && !data)) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;
  }
  if (!isAuthed) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="max-w-lg mx-auto text-center py-24 px-5"><Sparkles className="text-blaze mx-auto" /><h1 className="font-serif text-4xl text-bone mt-5">Year in Review</h1><p className="text-bone/60 mt-3">Sign in to see your season recap.</p><button onClick={() => openAuth("login")} className="mt-6 bg-blaze text-white px-6 py-3 rounded-full text-sm font-semibold" data-testid="yir-signin">Sign in</button></div></div>;
  }

  const years = data?.available_years?.length ? data.available_years : [String(now)];
  const empty = data && data.hunts === 0;

  return (
    <div className="bg-charcoal min-h-screen grain text-bone" data-testid="year-in-review-page">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className={overline}>Season recap</span>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2">Year in Review</h1>
          </div>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-white/5 border border-white/15 rounded px-4 py-2.5 text-bone outline-none focus:border-blaze" data-testid="yir-year">
            {years.map((y) => <option key={y} value={y} className="bg-charcoal">{y}</option>)}
          </select>
        </div>

        {empty ? (
          <div className="mt-10 text-center py-16 border border-dashed border-white/15 rounded-xl" data-testid="yir-empty">
            <p className="text-bone/60">No hunts logged for {year} yet. Log hunts in your Passport and your recap builds itself.</p>
            <Link to="/passport" className="mt-5 inline-block bg-blaze text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="yir-to-passport">Open Hunt Passport</Link>
          </div>
        ) : data && (
          <>
            {/* Shareable card */}
            <motion.div ref={cardRef} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="mt-8 rounded-2xl bg-gradient-to-b from-forest/30 via-charcoal to-charcoal border border-white/10 p-8 sm:p-10" data-testid="yir-card">
              <div className="text-center">
                <span className="hr-overline text-blaze">{data.display_name} · {data.year}</span>
                <h2 className="font-serif text-3xl sm:text-4xl mt-2">Your HomeRange Season</h2>
              </div>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
                <Big value={data.hunts} label="Hunts" />
                {data.days_known ? <Big value={data.days_afield} label="Days afield" /> : <Big value="—" label="Days afield" sub="when logged" />}
                <Big value={data.states.length} label="States" />
                <Big value={data.species.length} label="Species" />
              </div>
              {show.harvests && (
                <div className="mt-6 flex items-center justify-center gap-2 text-forest"><Award size={16} /> <span className="font-semibold">{data.harvests} harvest{data.harvests === 1 ? "" : "s"}</span></div>
              )}
              {(data.new_states.length > 0 || data.new_species.length > 0) && (
                <div className="mt-6 text-center text-sm text-bone/70">
                  {data.new_states.length > 0 && <p><span className="text-blaze font-semibold">New states:</span> {data.new_states.join(", ")}</p>}
                  {data.new_species.length > 0 && <p className="mt-1"><span className="text-blaze font-semibold">New species:</span> {data.new_species.join(", ")}</p>}
                </div>
              )}
              {show.partners && data.partners.length > 0 && (
                <div className="mt-6 text-center text-sm text-bone/70"><Users size={13} className="inline mr-1.5 text-bone/50" />{data.partners.length} hunting partner{data.partners.length === 1 ? "" : "s"}: {data.partners.join(", ")}</div>
              )}
              {show.photos && data.photos.length > 0 && (
                <div className="mt-7 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="yir-photos">
                  {data.photos.slice(0, 8).map((p, i) => <div key={i} className="aspect-square rounded-lg overflow-hidden bg-white/5"><img crossOrigin="anonymous" src={p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt={p.title || "Hunt"} className="w-full h-full object-cover" /></div>)}
                </div>
              )}
              <p className="mt-8 text-center text-bone/30 text-xs">homerangehunt.com</p>
            </motion.div>

            {/* Controls */}
            <div className="mt-6 flex flex-wrap items-center gap-3 justify-between">
              <div className="flex flex-wrap gap-4" data-testid="yir-toggles">
                {[["harvests", "Harvests"], ["partners", "Partners"], ["photos", "Photos"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-bone/70 cursor-pointer">
                    <input type="checkbox" checked={show[k]} onChange={(e) => setShow({ ...show, [k]: e.target.checked })} data-testid={`yir-toggle-${k}`} /> {l}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={share} className="inline-flex items-center gap-2 border border-white/15 text-bone/80 hover:text-bone px-5 py-2.5 rounded-full text-sm font-semibold transition-colors" data-testid="yir-share"><Share2 size={15} /> Share</button>
                <button onClick={download} disabled={exporting} className="inline-flex items-center gap-2 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="yir-download">{exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download</button>
              </div>
            </div>

            {/* Memorable hunts */}
            {data.memorable.length > 0 && (
              <div className="mt-10">
                <h3 className="font-serif text-2xl">Memorable hunts</h3>
                <div className="mt-4 space-y-2">
                  {data.memorable.map((m) => (
                    <Link key={m.hunt_id} to={`/hunts/${m.hunt_id}`} className="flex items-center justify-between bg-white/[0.04] border border-white/10 hover:border-blaze/50 rounded-xl px-4 py-3 transition-colors group" data-testid={`yir-memorable-${m.hunt_id}`}>
                      <span className="text-sm text-bone/85 flex items-center gap-2">{m.harvest && <Award size={14} className="text-forest" />}{m.title} <span className="text-bone/40">· {m.species}{m.state ? ` · ${m.state}` : ""}</span></span>
                      <ChevronRight size={16} className="text-bone/40 group-hover:text-blaze transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <p className="text-bone/40 text-xs mt-10 flex items-center gap-1.5"><Lock size={11} /> Recaps use only your real logged data and never include exact locations, notes, or application details. You choose what to share.</p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
